import { useMap } from "@vis.gl/react-maplibre";
import type { MapMouseEvent } from "maplibre-gl";
import { FC, RefObject, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { getAliveMap } from "@ryde/controls";
import { useGoogleServices } from "@ryde/redux";
import {
  appLanguageSelector,
  createErrorResult,
  errors,
  IStationRouteWaypointLocationDraft,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { isStationRouteWaypointMapUiClick } from "./station-route-waypoint-map-click.utils";

interface IStationRouteWaypointMapClickProps {
  isActive: boolean;
  onLocationPicked: (draft: IStationRouteWaypointLocationDraft) => void;
  toolbarRef: RefObject<HTMLDivElement | null>;
  popperContentRef: RefObject<HTMLDivElement | null>;
}

export const StationRouteWaypointMapClick: FC<
  IStationRouteWaypointMapClickProps
> = ({ isActive, onLocationPicked, toolbarRef, popperContentRef }) => {
  const { current: mapRef } = useMap();
  const { searchAddressByGeo } = useGoogleServices();
  const language = useRecoilValue(appLanguageSelector);
  const { t } = useTranslation();

  const handleLocationClicked = useEvent(async (lat: number, lng: number) => {
    try {
      const placeResult = await searchAddressByGeo({
        geoParams: { location: { lat, lng }, language },
      });

      if (!placeResult.success) {
        warnAndToastSimpleResult(placeResult, t);
        return;
      }

      onLocationPicked({
        fullAddress: placeResult.data?.text?.text ?? "",
        latitude: lat,
        longitude: lng,
      });
    } catch {
      warnAndToastSimpleResult(
        createErrorResult(
          errors.common.technicalProblemOccurred,
          "Error while resolving waypoint location",
        ),
        t,
      );
    }
  });

  useEffect(() => {
    const map = getAliveMap(mapRef);

    if (!map || !isActive) {
      return undefined;
    }

    const canvas = map.getCanvas();
    canvas.style.cursor = "crosshair";

    const onClick = (event: MapMouseEvent) => {
      const target = event.originalEvent?.target;

      if (target instanceof Node) {
        if (
          toolbarRef.current?.contains(target) ||
          popperContentRef.current?.contains(target)
        ) {
          return;
        }
      }

      if (isStationRouteWaypointMapUiClick(event)) {
        return;
      }

      const { lat, lng } = event.lngLat;
      handleLocationClicked(lat, lng);
    };

    map.on("click", onClick);
    return () => {
      const aliveMap = getAliveMap(mapRef);
      if (!aliveMap) {
        return;
      }

      aliveMap.off("click", onClick);
      canvas.style.cursor = "";
    };
  }, [handleLocationClicked, isActive, mapRef, popperContentRef, toolbarRef]);

  return null;
};
